import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatConversionRate, getStageName } from '../../../../lib/utils/conversionFunnelUtils';
import { brandColors } from '../../../../theme/theme';

/**
 * DemographicConversionComparison Component
 * 
 * Compares conversion rates across different demographic segments
 * Shows grouped/stacked bar charts for side-by-side comparison
 */
const DemographicConversionComparison = ({ demographicBreakdowns, colorSchemes }) => {
  const [selectedStage, setSelectedStage] = useState(2); // Default to Stage 2 (Interested)
  const [comparisonMode, setComparisonMode] = useState('side-by-side'); // 'side-by-side' or 'stacked'

  if (!demographicBreakdowns || demographicBreakdowns.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No demographic data available for comparison
        </Typography>
      </Paper>
    );
  }

  // Build comparison data for the selected stage
  const buildComparisonData = () => {
    const comparisonData = [];

    demographicBreakdowns.forEach(demo => {
      const stage = demo.stages.find(s => s.stage === selectedStage);
      if (!stage) return;

      Object.entries(stage.segments).forEach(([segment, metrics]) => {
        let dataPoint = comparisonData.find(d => d.segment === segment);
        if (!dataPoint) {
          dataPoint = { segment };
          comparisonData.push(dataPoint);
        }
        dataPoint[demo.demographic] = metrics.conversion_from_previous_stage || 0;
      });
    });

    return comparisonData.sort((a, b) => {
      // Sort by first demographic's value
      const firstDemo = demographicBreakdowns[0].demographic;
      return (b[firstDemo] || 0) - (a[firstDemo] || 0);
    });
  };

  const comparisonData = buildComparisonData();

  // Get available stages (2-5, as stage 1 doesn't have conversion rate)
  const availableStages = [2, 3, 4, 5];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2">
                <strong>{entry.name}:</strong> {formatConversionRate(entry.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Demographic Conversion Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare conversion rates across different demographic segments
        </Typography>
      </Box>

      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Conversion Stage</InputLabel>
            <Select
              value={selectedStage}
              label="Conversion Stage"
              onChange={(e) => setSelectedStage(e.target.value)}
            >
              {availableStages.map(stage => (
                <MenuItem key={stage} value={stage}>
                  {getStageName(stage - 1)} → {getStageName(stage)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Display Mode</InputLabel>
            <Select
              value={comparisonMode}
              label="Display Mode"
              onChange={(e) => setComparisonMode(e.target.value)}
            >
              <MenuItem value="side-by-side">Side by Side</MenuItem>
              <MenuItem value="stacked">Stacked</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Chart */}
      {comparisonData.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={comparisonData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="segment" 
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis 
                label={{ 
                  value: 'Conversion Rate (%)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              {demographicBreakdowns.map((demo, index) => (
                <Bar
                  key={demo.demographic}
                  dataKey={demo.demographic}
                  fill={[brandColors.primary, brandColors.secondary, brandColors.info, brandColors.success][index % 4]}
                  name={demo.demographic.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  stackId={comparisonMode === 'stacked' ? 'a' : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No conversion data available for this stage
          </Typography>
        </Box>
      )}

      {/* Insights */}
      {comparisonData.length > 0 && (
        <Box sx={{ p: 2, backgroundColor: `${brandColors.info}10`, borderRadius: 2, border: `1px solid ${brandColors.info}40` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: brandColors.info }}>
            Key Findings
          </Typography>
          <Typography variant="body2">
            {(() => {
              // Find best and worst segments across all demographics
              const allRates = [];
              comparisonData.forEach(item => {
                demographicBreakdowns.forEach(demo => {
                  if (item[demo.demographic]) {
                    allRates.push({
                      segment: item.segment,
                      demographic: demo.demographic,
                      rate: item[demo.demographic]
                    });
                  }
                });
              });

              allRates.sort((a, b) => b.rate - a.rate);
              const best = allRates[0];
              const worst = allRates[allRates.length - 1];

              if (best && worst) {
                return `Best performer: ${best.segment} in ${best.demographic} (${formatConversionRate(best.rate)}). 
                        Lowest: ${worst.segment} in ${worst.demographic} (${formatConversionRate(worst.rate)}). 
                        Focus marketing efforts on underperforming segments to improve overall conversion.`;
              }
              return 'Review the chart to identify optimization opportunities.';
            })()}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DemographicConversionComparison;

